import useDynamicDialogSettings from './useDynamicDialogSettings'

/**
 * Dialog settings for a modal that draws its own body and footer sections.
 *
 * This is a SECOND, opt-in shape rather than a change to
 * `useDynamicDialogSettings`. 21 dialogs still use that one and rely on
 * PrimeVue's content padding for their spacing; switching it underneath them
 * would re-lay-out half the app in one commit, unreviewed.
 *
 * Used today by the shift open and close dialogs (`stores/shared/shift.ts`).
 *
 * ## What it changes
 *
 * - `contentStyle: { padding: '0' }` — the content wrapper carries no padding,
 *   so the component's own sections can reach both edges.
 * - `class: 'p-dialog-sectioned'` — restyles PrimeVue's header to match those
 *   sections: `px-6 py-4` instead of `p-5`, `text-xl font-bold` instead of
 *   `text-xl font-semibold`, plus a bottom divider. The rule lives in
 *   `assets/primevue/dialog.css`, scoped to that class so nothing else moves.
 *
 * `contentStyle` rather than `pt`: the pass-through object does NOT reach the
 * component through DynamicDialog — the rendered element carried no extra
 * class. `contentStyle` is a plain Dialog prop and binds.
 *
 * ## The contract
 *
 * - **The component supplies all padding.** Nothing is inherited. Every
 *   section needs its own `px-6` (or whatever it wants).
 * - **PrimeVue still owns the title.** Keep passing `header` in the dialog
 *   props. Do not draw a second title inside the component — that stacks two
 *   titles for the same thing, which is what it did before this was fixed.
 *
 * What it buys: dividers that reach both edges. With inherited padding they
 * stop short of the frame, and pulling them back out with a negative margin
 * only works if the two paddings happen to match. They did not — the outer was
 * 20px, the negative margin 24px — so the footer overflowed and the dialog
 * grew a horizontal scrollbar.
 *
 * ## Migrating an existing dialog
 *
 * Do this when you are already working in a dialog, not as a sweep. Each one
 * is a small, self-contained change, and the result has to be looked at.
 *
 * 1. Swap the import and the spread: `useDynamicDialogSettings` →
 *    `useSectionedDialogSettings`. Keep the `header` prop as it is.
 * 2. The component's content will now sit flush against the frame. Wrap the
 *    body in `<div class="px-6 py-5">`.
 * 3. If it has a footer or action row, make it a SIBLING of that body div —
 *    not a child — with `class="flex items-center gap-3 border-0 border-t
 *    border-solid border-gray-100 px-6 py-4"`. The `border-0` is not
 *    decoration: this project disables Tailwind's preflight
 *    (`tailwind.config.js:6`), so a bare `border-t` leaves the other three
 *    sides at the CSS initial `medium` — 3px. See `docs/PARKED.md`.
 * 4. A FormKit submit carries `mb-4` from its theme. Inside a footer row that
 *    reads as a stray gap: add `outer-class="!mb-0"`.
 * 5. Open it and look at it. Check the dividers reach both edges and that the
 *    dialog has no horizontal scrollbar.
 *
 * Once every dialog has moved, fold these settings into
 * `useDynamicDialogSettings` and delete this file.
 */
export default {
  ...useDynamicDialogSettings,
  contentStyle: { padding: '0' },
  class: 'p-dialog-sectioned',
}
